import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import App from './App.tsx'
import './index.css'

const DASHBOARD_THEME_STORAGE_KEY = 'reach_dashboard_theme';
const initialTheme = 'ocean';
document.documentElement.setAttribute('data-theme', initialTheme);
localStorage.setItem(DASHBOARD_THEME_STORAGE_KEY, initialTheme);

const sentryEnabled = !import.meta.env.DEV && !!import.meta.env.VITE_SENTRY_DSN;

if (sentryEnabled) {
  Sentry.init({ dsn: import.meta.env.VITE_SENTRY_DSN, environment: import.meta.env.MODE })
}

const SHOPIFY_TOKEN_STORAGE_KEY = 'reach_shopify_session_token';
const WORDPRESS_TOKEN_STORAGE_KEY = 'reach_wordpress_session_token';
const PREVIEW_BEARER_STORAGE_KEY = 'reach_preview_bearer_token';
const API_URL = import.meta.env.VITE_API_URL || '';

const _originalFetch = window.fetch.bind(window);

const urlParams = new URLSearchParams(window.location.search);
const tokenFromUrl = urlParams.get('shopify_session_token');
if (tokenFromUrl) {
  window.sessionStorage.setItem(SHOPIFY_TOKEN_STORAGE_KEY, tokenFromUrl);
}
const wpTokenFromUrl = urlParams.get('wp_session_token');
if (wpTokenFromUrl) {
  window.sessionStorage.setItem(WORDPRESS_TOKEN_STORAGE_KEY, wpTokenFromUrl);
}

// Preview token: redeem one-time handoff token → scoped bearer token
// The handoff token is short-lived (60s); we exchange it immediately and never store it.
const previewHandoffToken = urlParams.get('preview_session_token');
// Holds the in-flight redeem promise; /api/merchant/* fetches await this when present.
let previewReadyPromise: Promise<void> | null = null;
// Holds a user-visible error if redemption fails, so the app can render an error state.
let previewError: string | null = null;

if (previewHandoffToken) {
  // Strip token from URL immediately to keep it out of browser history
  urlParams.delete('preview_session_token');
  const cleanUrl = [window.location.pathname, urlParams.toString() ? '?' + urlParams.toString() : ''].join('');
  window.history.replaceState({}, '', cleanUrl);

  previewReadyPromise = _originalFetch(`${API_URL}/api/preview/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: previewHandoffToken }),
  }).then(async (res) => {
    if (!res.ok) {
      previewError = res.status === 401
        ? 'Preview link has expired. Please generate a new one from the console.'
        : `Preview token redemption failed (${res.status}).`;
      throw new Error(previewError);
    }
    const data = await res.json() as { bearerToken?: string };
    if (data.bearerToken) {
      window.sessionStorage.setItem(PREVIEW_BEARER_STORAGE_KEY, data.bearerToken);
    }
  }).catch((err) => {
    if (!previewError) previewError = 'Preview link expired or invalid.';
    console.error('[Reach Preview] Token redemption failed:', err);
  });
  (window as any).__reachPreviewReady = previewReadyPromise;
  (window as any).__reachPreviewError = () => previewError;
}

window.fetch = (input, init = {}) => {
  const url = typeof input === 'string' ? input
    : input instanceof URL ? input.toString()
    : (input as Request).url;
  if (url.includes('/api/merchant')) {
    // If a preview redeem is in flight, wait for it before sending the merchant request.
    // This prevents a race where early fetches fire before the bearer token is stored.
    if (previewReadyPromise) {
      return previewReadyPromise.then(() => {
        const headers = new Headers((init as RequestInit).headers);
        const previewBearerToken = window.sessionStorage.getItem(PREVIEW_BEARER_STORAGE_KEY);
        if (previewBearerToken) headers.set('Authorization', `Bearer ${previewBearerToken}`);
        return _originalFetch(input, { ...init, headers });
      });
    }
    const headers = new Headers((init as RequestInit).headers);
    const shopifySessionToken = window.sessionStorage.getItem(SHOPIFY_TOKEN_STORAGE_KEY);
    const wordpressSessionToken = window.sessionStorage.getItem(WORDPRESS_TOKEN_STORAGE_KEY);
    if (shopifySessionToken) {
      headers.set('Authorization', `Bearer ${shopifySessionToken}`);
    } else if (wordpressSessionToken) {
      headers.set('Authorization', `Bearer ${wordpressSessionToken}`);
    }
    init = { ...init, headers };
  }
  
  return _originalFetch(input, init).then((res) => {
    if (res.status === 451) {
      window.dispatchEvent(new CustomEvent('reach:legal-consent-required'));
    }
    return res;
  });
};

const app = sentryEnabled ? (
  <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
    <App />
  </Sentry.ErrorBoundary>
) : (
  <App />
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>{app}</React.StrictMode>,
)
